import { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { Send, Trash2, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { api, getCurriculum, streamTutor } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { SpeakButton } from "@/components/SpeakButton";
import { useOnline } from "@/hooks/useOnline";

const STARTERS = ["Hi! Can we practise introductions?", "Help me order food at a café", "Ask me about my weekend", "Teach me a useful phrase"];

const Bubble = ({ m, lang, i }) => {
  const mine = m.role === "user";
  return (
    <div className={`flex ${mine ? "justify-end" : "justify-start"} animate-fade-up`}>
      <div data-testid={`chat-message-${i}`} className={`max-w-[85%] rounded-3xl px-5 py-3 ${mine ? "bg-[hsl(var(--primary))] text-white rounded-br-md" : "bg-white border border-slate-200 text-slate-800 rounded-bl-md"}`}>
        <p className="whitespace-pre-wrap leading-relaxed">{m.content || "…"}</p>
        {!mine && m.content && <div className="mt-2 -ml-1"><SpeakButton text={m.content.replace(/Correction:.*\n?/i, "")} lang={lang} testId={`chat-speak-${i}`} /></div>}
      </div>
    </div>
  );
};

export default function Tutor() {
  const { lang } = useParams();
  const { refreshUser } = useAuth();
  const [msgs, setMsgs] = useState([]);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [name, setName] = useState("");
  const endRef = useRef(null);
  const online = useOnline();

  useEffect(() => {
    api.get(`/tutor/${lang}/messages`).then(({ data }) => setMsgs(data));
    getCurriculum().then((c) => setName(c.languages.find((l) => l.code === lang)?.name || ""));
  }, [lang]);
  useEffect(() => endRef.current?.scrollIntoView({ behavior: "smooth" }), [msgs]);

  const send = async (message) => {
    if (!message.trim() || busy) return;
    setText("");
    setBusy(true);
    setMsgs((m) => [...m, { role: "user", content: message }, { role: "assistant", content: "" }]);
    try {
      await streamTutor(lang, message, (d) =>
        setMsgs((m) => m.map((x, i) => (i === m.length - 1 ? { ...x, content: x.content + d } : x)))
      );
      refreshUser();
    } catch (e) {
      toast.error(e.message);
      setMsgs((m) => m.slice(0, -1));
    } finally {
      setBusy(false);
    }
  };

  const clear = async () => {
    await api.delete(`/tutor/${lang}/messages`);
    setMsgs([]);
  };

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 py-6 flex flex-col h-[calc(100vh-4rem)]">
      <div className="flex items-center gap-3 mb-4">
        <span className="h-11 w-11 rounded-2xl bg-[hsl(var(--secondary-light))] grid place-items-center"><Sparkles className="text-[hsl(var(--secondary))]" /></span>
        <div className="flex-1">
          <h1 className="font-display text-xl font-extrabold text-slate-900">Lumi, your {name} tutor</h1>
          <p className="text-xs text-slate-500">Chats at your level and gently corrects mistakes in English.</p>
        </div>
        {msgs.length > 0 && <button data-testid="chat-clear-button" onClick={clear} className="h-9 w-9 grid place-items-center rounded-full text-slate-400 hover:bg-slate-100 hover:text-rose-600"><Trash2 size={17} /></button>}
      </div>
      <div data-testid="chat-messages" className="flex-1 overflow-y-auto space-y-4 pr-1 pb-4">
        {msgs.length === 0 && (
          <div className="text-center py-12">
            <p className="text-slate-600 mb-5">Say anything, in {name} or English. Try one of these:</p>
            <div className="flex flex-wrap gap-2 justify-center">
              {STARTERS.map((s, i) => (
                <button key={s} data-testid={`chat-starter-${i}`} onClick={() => send(s)} className="word-pill hover:bg-[hsl(var(--primary-light))]">{s}</button>
              ))}
            </div>
          </div>
        )}
        {msgs.map((m, i) => <Bubble key={i} m={m} i={i} lang={lang} />)}
        <div ref={endRef} />
      </div>
      {!online && <p data-testid="tutor-offline-note" className="text-sm text-center font-semibold text-slate-700 bg-slate-100 rounded-2xl py-2 mb-2">Lumi needs internet to chat. Your past conversation is still here to read and listen to.</p>}
      <form onSubmit={(e) => { e.preventDefault(); send(text); }} className="flex gap-2 pt-3 border-t border-slate-200">
        <input
          data-testid="chat-input"
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder={`Write in ${name || "your language"}...`}
          className="flex-1 rounded-full border-2 border-slate-200 focus:border-[hsl(var(--primary))] focus:outline-none px-5 py-3 bg-white"
        />
        <button data-testid="chat-send-button" disabled={busy || !text.trim() || !online} className="btn-primary !rounded-full !px-5"><Send size={18} /></button>
      </form>
    </div>
  );
}
